export type GraphType = 'bar' | 'line';

export interface ReportsFeedData {
	feedId?: string;
	itemId?: string;
	subscriptionId?: string;
	accountId?: string;
	fileName: string;
	filePath: string[];
	fullName: string;
	status: string;
	statusReason: string;
	ownerNames: string[];
	lastModified: string;
	tags: Record<string, number>;
	sharingLevels: string[];
	cloudType: string;
	violations: number;
	resolvedBy: string | null;
	size: number;
	fileExtension: string;
	significantFindingCategories: string[];
	_groupFieldValue?: any;
}

type ChartLeafValue = number;

type GroupedChartData = {
  [key: string]: ChartLeafValue | GroupedChartData;
};
export interface GroupedTableData {
	data: Array<{
		groupFieldName: string;
		groupFieldValue: string;
		data: ReportsFeedData[];
		count: number;
		totalCount: number;
	}>;
	count: number;
	totalCount: number;
	isGrouped: true;
	baseDataType: string;
}
export interface FlatTableData {
	data: ReportsFeedData[];
	count: number;
	totalCount: number;
	isGrouped: boolean;
	baseDataType: string;
}

export type TableData = FlatTableData | GroupedTableData;
export interface ChartData {
	data: GroupedChartData;
	isGrouped: boolean;
	fieldNames: string[];
}

export interface UnifiedReportingResponse {
	table: TableData;
	chart: ChartData;
}

export enum FilterOperation {
	CONTAINS = 'CONTAINS',
	NOT_CONTAINS = 'NOT_CONTAINS',
	BLANK = 'BLANK',
	NOT_BLANK = 'NOT_BLANK',
	EQUALS = 'EQUALS',
	GREATER = 'GREATER',
	BETWEEN = 'BETWEEN',
	LESS = 'LESS',
	DATE_EQUALS = 'DATE_EQUALS',
	IN = 'IN',
}

export interface ReportingState {
	tableData: TableData | null;
	chartData: ChartData | null;
	loading: boolean;
	initialLoading: boolean;
	chartDataReceived: boolean;
	error: string | null;
	activeGraph: ChartType;
	activeReport: ActiveReport;
	reportsMetaData: ReportsMetaDataResponse | null;
	activeChartConfig: ReportConfig;
	page: number;
	pageSize: number;
	pinnedReport: string | null;
	toast: {
		message: string;
		type: 'success' | 'info' | 'warning' | 'error' | 'default';
	};
}

export interface GraphState {
	activeGraph: GraphType;
}

export enum ChartType {
	VERTICAL_BAR = 'VERTICAL_BAR',
	HORIZONTAL_BAR = 'HORIZONTAL_BAR',
	HORIZONTAL_STACKED_BAR = 'HORIZONTAL_STACKED_BAR',
	HORIZONTAL_GROUPED_BAR = 'HORIZONTAL_GROUPED_BAR',
	LINE = 'LINE',
	TABLE = 'TABLE',
}

export enum ChartPeriod {
	DAILY = 'DAILY',
	WEEKLY = 'WEEKLY',
	MONTHLY = 'MONTHLY',
	HOURLY = 'HOURLY',
}

export interface ChartConfig {
	chartType: ChartType;
	chartPeriod: ChartPeriod;
	searchQuery: string;
	absoluteDateRangeStart: Date;
	absoluteDateRangeEnd: Date;
	topDisplayCount: number;
	groupBy: string[];
	sortBy: string[];
	hiddenColumns: string[];
}

export interface ReportsMetaDataResponse {
	reports: {
		pinnedReport: string | null;
		reports: Report[];
	};
	fields: {
		DETECTIONS: DetectionFields;
	};
}

export interface ReportResponseById {
	reportType: string;
    id: string;
    accountId: string;
    name: string;
    lastModifiedBy: string | null;
    lastModifiedOn: string | null;
    createdOn: string;
    baseDataType: string;
    reportConfig: ReportConfig;
}
export interface ReportByIdApiResponse {
    data: ReportResponseById;
    elapsed?: number;
}

export interface PinReportApiResponse {
    data: {
        reportId: string;
    };
    elapsed?: number;
}

export type PinReportPayload = {
    reportId: string;
};

export interface ActiveReport {
	type: string;
	reportId: string;
	name: string;
}

export interface Report {
	type: string;
	reportId: string;
	name: string;
	lastModifiedBy: string | null;
	lastModifiedOn: string | null;
	createdOn: string;
	baseDataType: string;
	config: ReportConfig;
}

export interface ReportConfig {
    chartType: ChartType;
    chartPeriod: ChartPeriod;
    searchQuery: string | null;
    relativeDateRange: string | null;
    absoluteDateRangeStart: string | null;
    absoluteDateRangeEnd: string | null;
    topDisplayCount: number;
    groupBy: string[];
    sortBy: string[];
    filters: {
        fieldName: string;
        value: any;
        operation: FilterOperation;
    }[];
    hiddenColumns: string[];
}

export interface DetectionFields {
	[key: string]: Field;
}

export interface Field {
	name: string;
	type: string;
	possibleValues: Record<string, string> | null;
}
